import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, User, MessageCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import { Logo } from '../components/Logo';

interface Message {
  id: string;
  content: string;
  sender_id: string;
  created_at: string;
  read: boolean;
  sender: {
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
}

interface ChatUser {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Chat {
  id: string;
  updated_at: string;
  other_user: ChatUser;
  last_message?: {
    content: string;
    created_at: string;
    sender_id: string;
    read: boolean;
  };
}

function ChatView() {
  const { chatId } = useParams();
  const { user: currentUser } = useUser();
  const [chats, setChats] = useState<Chat[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingChats, setLoadingChats] = useState(true);
  const [otherUser, setOtherUser] = useState<ChatUser | null>(null);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (currentUser) {
      fetchChats();
      const subscription = subscribeToChats();
      return () => {
        subscription();
      };
    }
  }, [currentUser]);

  useEffect(() => {
    if (chatId && currentUser) {
      fetchMessages();
      fetchChatUsers();
      const subscription = subscribeToNewMessages();
      markMessagesAsRead();

      return () => {
        subscription();
      };
    }
  }, [chatId, currentUser]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChats = async () => {
    if (!currentUser) return;

    try {
      const { data, error } = await supabase
        .from('chats')
        .select(`
          id,
          updated_at,
          user1:users!chats_user1_id_fkey(id, username, display_name, avatar_url),
          user2:users!chats_user2_id_fkey(id, username, display_name, avatar_url),
          messages:messages(
            content,
            created_at,
            sender_id,
            read
          )
        `)
        .or(`user1_id.eq.${currentUser.id},user2_id.eq.${currentUser.id}`)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const formattedChats: Chat[] = data.map(chat => ({
        id: chat.id,
        updated_at: chat.updated_at,
        other_user: chat.user1.id === currentUser.id ? chat.user2 : chat.user1,
        last_message: chat.messages[0]
      }));

      setChats(formattedChats);
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      setLoadingChats(false);
    }
  };

  const subscribeToChats = () => {
    const channel = supabase
      .channel('chat_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chats'
        },
        () => {
          fetchChats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const fetchChatUsers = async () => {
    if (!chatId || !currentUser) return;

    try {
      const { data: chat, error } = await supabase
        .from('chats')
        .select(`
          user1:users!chats_user1_id_fkey(id, username, display_name, avatar_url),
          user2:users!chats_user2_id_fkey(id, username, display_name, avatar_url)
        `)
        .eq('id', chatId)
        .single();

      if (error) throw error;

      const otherUserData = chat.user1.id === currentUser.id ? chat.user2 : chat.user1;
      setOtherUser(otherUserData);
    } catch (error) {
      console.error('Error fetching chat users:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select(`
          id,
          content,
          sender_id,
          created_at,
          read,
          sender:users(username, display_name, avatar_url)
        `)
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNewMessages = () => {
    const channel = supabase
      .channel(`chat:${chatId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`
        },
        () => {
          fetchMessages();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const markMessagesAsRead = async () => {
    if (!currentUser) return;

    try {
      await supabase
        .from('messages')
        .update({ read: true })
        .eq('chat_id', chatId)
        .neq('sender_id', currentUser.id)
        .eq('read', false);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !chatId || sending) return;

    setSending(true);
    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          chat_id: chatId,
          sender_id: currentUser.id,
          content: newMessage.trim(),
        });

      if (error) throw error;
      setNewMessage('');
      await fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!chatId) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="mobile-header">
          <div className="mobile-header-content">
            <div className="flex items-center gap-2">
              <Logo className="h-8 w-8 text-indigo-600" />
              <span className="text-lg font-bold text-indigo-600">Chat</span>
            </div>
          </div>
        </header>

        <div className="mobile-main pt-2">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-lg shadow-md">
              <div className="p-4 border-b">
                <h2 className="text-xl font-semibold">Messages</h2>
              </div>

              {loadingChats ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : chats.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No messages yet</h3>
                  <p className="text-gray-600">
                    Start a conversation by visiting someone's profile
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {chats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => navigate(`/chat/${chat.id}`)}
                      className="w-full p-4 hover:bg-gray-50 transition-colors flex items-center space-x-4"
                    >
                      <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                        {chat.other_user.avatar_url ? (
                          <img
                            src={chat.other_user.avatar_url}
                            alt={chat.other_user.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          <h3 className="font-semibold truncate">
                            {chat.other_user.display_name || chat.other_user.username}
                          </h3>
                          <span className="text-xs text-gray-500">
                            {new Date(chat.updated_at).toLocaleString()}
                          </span>
                        </div>
                        {chat.last_message && (
                          <p className="text-sm text-gray-600 truncate">
                            {chat.last_message.sender_id === currentUser.id ? 'You: ' : ''}
                            {chat.last_message.content}
                          </p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="mobile-header">
        <div className="mobile-header-content">
          <div className="flex items-center gap-2">
            <Link to="/chat" className="text-gray-600 hover:text-indigo-600">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            {otherUser && (
              <Link to={`/profile/${otherUser.id}`} className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden">
                  {otherUser.avatar_url ? (
                    <img
                      src={otherUser.avatar_url}
                      alt={otherUser.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-400" />
                    </div>
                  )}
                </div>
                <div>
                  <span className="font-semibold">
                    {otherUser.display_name || otherUser.username}
                  </span>
                </div>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="mobile-main pt-2">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md flex flex-col h-[calc(100vh-8rem)]">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${
                        message.sender_id === currentUser?.id ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      <div
                        className={`max-w-[70%] ${
                          message.sender_id === currentUser?.id
                            ? 'bg-indigo-600 text-white rounded-l-lg rounded-tr-lg'
                            : 'bg-gray-100 text-gray-900 rounded-r-lg rounded-tl-lg'
                        } p-3`}
                      >
                        <p>{message.content}</p>
                        <p
                          className={`text-xs mt-1 ${
                            message.sender_id === currentUser?.id
                              ? 'text-indigo-200'
                              : 'text-gray-500'
                          }`}
                        >
                          {new Date(message.created_at).toLocaleString()}
                          {message.sender_id !== currentUser?.id && (
                            <span className="ml-2">
                              {message.read ? '• Read' : ''}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 border-t">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  type="submit"
                  disabled={sending}
                  className={`p-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors ${
                    sending ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <Send className="h-5 w-5" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChatView;